import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getInventoryBatch, WRITE_OFF_REASONS } from '../lib/api';

/**
 * Printable batch receipt — the legacy refill/write-off slip rebuilt as a
 * standalone route. Opens in a new window from the batch dialog / batches log,
 * auto-fires the browser print dialog, and uses minimal black-on-white styles
 * tuned for an A5/half-letter print. Tailwind print: utilities hide the SPA
 * chrome around this view in case it's printed from inside the app shell.
 */
export function BranchBatchPrintPage() {
  const { id, batchId } = useParams<{ id: string; batchId: string }>();
  const q = useQuery({
    queryKey: ['admin-inventory-batch', id, batchId],
    queryFn: () => getInventoryBatch(id!, batchId!),
    enabled: !!id && !!batchId,
  });

  useEffect(() => {
    if (q.data) {
      const t = window.setTimeout(() => window.print(), 250);
      return () => window.clearTimeout(t);
    }
  }, [q.data]);

  if (q.isLoading) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  if (!q.data) return <p className="p-6 text-sm text-destructive">Batch not found.</p>;

  const b = q.data;
  const isReceive = b.kind === 'receive';
  const reasonLabel = WRITE_OFF_REASONS.find((r) => r.code === b.reason)?.label ?? b.reason;
  const created = new Date(b.createdAt);

  return (
    <div className="bg-white text-black mx-auto max-w-2xl p-8 print:p-6 font-[ui-sans-serif] text-[13px] leading-snug">
      <header className="flex items-start justify-between border-b border-black pb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">
            {isReceive ? 'Receive shipment' : 'Write-off report'}
          </div>
          <div className="text-lg font-semibold mt-0.5">{b.branchName}</div>
          <div className="text-[11px] text-neutral-600 font-mono">{b.branchCode}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">Ref number</div>
          <div className="text-lg font-mono font-semibold mt-0.5">{b.refNumber}</div>
          <div className="text-[11px] text-neutral-600 mt-1">
            {created.toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-4 mt-4 text-[12px]">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">Prepared by</div>
          <div className="mt-0.5">{b.actorName ?? b.actorEmail ?? ''}</div>
        </div>
        {!isReceive && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">Reason</div>
            <div className="mt-0.5 capitalize">{reasonLabel}</div>
          </div>
        )}
        {b.memo && (
          <div className="col-span-2">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">Memo</div>
            <div className="mt-0.5">{b.memo}</div>
          </div>
        )}
      </section>

      <table className="w-full mt-5 text-[12px] border-t border-b border-black">
        <thead>
          <tr className="border-b border-neutral-400">
            <th className="text-left py-1.5 pr-2 font-medium">SKU</th>
            <th className="text-left py-1.5 pr-2 font-medium">Product</th>
            <th className="text-right py-1.5 pr-2 font-medium">Qty</th>
            <th className="text-right py-1.5 font-medium">Balance after</th>
          </tr>
        </thead>
        <tbody>
          {b.lines.map((l) => (
            <tr key={l.movementId} className="border-b border-neutral-200 last:border-0">
              <td className="py-1.5 pr-2 font-mono text-[11px]">{l.variantSku ?? l.productSku}</td>
              <td className="py-1.5 pr-2">
                {l.productName}
                {l.variantName && <span className="text-neutral-500"> — {l.variantName}</span>}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {isReceive ? '+' : '−'}
                {Math.abs(l.delta)}
              </td>
              <td className="py-1.5 text-right tabular-nums">{l.balanceAfter}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} className="py-2 text-right font-medium">
              Total
            </td>
            <td className="py-2 text-right font-semibold tabular-nums">
              {isReceive ? '+' : '−'}
              {b.unitCount}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>

      <section className="grid grid-cols-2 gap-8 mt-12 text-[11px]">
        <div>
          <div className="border-t border-black pt-1">Prepared by</div>
          <div className="mt-0.5 text-neutral-600">{b.actorName ?? b.actorEmail ?? ''}</div>
        </div>
        <div>
          <div className="border-t border-black pt-1">Received / approved by</div>
          <div className="mt-0.5 text-neutral-600">&nbsp;</div>
        </div>
      </section>

      <div className="mt-6 flex justify-end print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-neutral-50"
        >
          Print again
        </button>
      </div>
    </div>
  );
}
